import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';

const MAX_DISCONNECT_SECONDS = 10800;

// The disconnect delay threshold is added to account for delays in identifying
// disconnections (for example, the websocket disconnection takes up to 3 seconds)
// while fetching the latest updated conversations or messages.
const DISCONNECT_DELAY_THRESHOLD = 15;

// 定时更新对话列表
const INCREMENTAL_SYNC_INTERVAL = 120000; // 120 秒轮询间隔

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    this.incrementalSyncTimer = null;

    this.setupEventListeners();
    this.setupNetworkListeners();
    this.startIncrementalSync();
  }

  disconnect = () => {
    this.removeEventListeners();
    this.stopIncrementalSync();
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  // 新增：设置网络状态监听
  setupNetworkListeners = () => {
    window.addEventListener('online', this.handleNetworkRecover);
    window.addEventListener('offline', this.handleNetworkDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    window.removeEventListener('online', this.handleNetworkRecover);
    window.removeEventListener('offline', this.handleNetworkDisconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  // 新增：处理网络恢复
  handleNetworkRecover = () => {
    // 只有在WebSocket未断开的情况下才需要主动刷新黑名单
    // 如果WebSocket断开了，重连完成时会自动刷新
    if (!BaseActionCableConnector.isDisconnected) {
      // console.log('[ReconnectService] WebSocket正常，主动刷新黑名单状态');
      emitter.emit(BUS_EVENTS.NETWORK_RECOVERED_REFRESH_BLACKLIST);
    }
  };
  // 新增：处理网络断开
  handleNetworkDisconnect = () => {
    // console.log('Network disconnected');
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
    }
  };

  fetchConversations = async () => {
    await this.store.dispatch('updateChatListFilters', {
      page: null,
      updatedWithin:
        this.getSecondsSinceDisconnect() + DISCONNECT_DELAY_THRESHOLD,
    });
    await this.store.dispatch('fetchAllConversations');
    // Reset the updatedWithin in the store chat list filter after fetching conversations when the user is reconnected
    await this.store.dispatch('updateChatListFilters', {
      updatedWithin: null,
    });
  };

  fetchFilteredOrSavedConversations = async queryData => {
    await this.store.dispatch('fetchFilteredConversations', {
      queryData,
      page: 1,
    });
  };

  fetchConversationsOnReconnect = async () => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations();
    }
  };

  fetchConversationMessagesOnReconnect = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversationId),
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect();
      await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    } else if (isNotificationRoute(currentRoute)) {
      await this.fetchNotificationsOnReconnect();
    }
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime = new Date();
    this.setConversationLastMessageId();
  };

  onReconnect = async () => {
    await this.handleRouteSpecificFetch();
    await this.revalidateCaches();
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
  };

  // 定时获取最近更新的对话，弥补 WebSocket 消息丢失
  startIncrementalSync = () => {
    this.stopIncrementalSync();

    this.incrementalSyncTimer = setInterval(() => {
      this.fetchIncrementalUpdates();
    }, INCREMENTAL_SYNC_INTERVAL);
  };

  stopIncrementalSync = () => {
    if (this.incrementalSyncTimer) {
      clearInterval(this.incrementalSyncTimer);
      this.incrementalSyncTimer = null;
    }
  };

  fetchIncrementalUpdates = async () => {
    // 条件检查：页面可见时才同步
    if (document.visibilityState !== 'visible') {
      return;
    }

    // 检查当前路由是否在对话相关页面
    const currentRoute = this.router.currentRoute.value.name;
    if (!isAConversationRoute(currentRoute, true)) {
      return;
    }

    // 如果当前选中的联系人处于黑名单状态，无须进行定时器更新
    const selectedChat = this.store.getters.getSelectedChat;
    const contactId = selectedChat?.meta?.sender?.id;
    if (contactId) {
      const isBlacklisted = this.store.getters['contacts/isContactBlacklisted'](contactId);
      if (isBlacklisted) {
        return;
      }
    }

    try {
      await this.store.dispatch('scheduledFetchConversationsAndMessages');
    } catch (error) {
      // 静默处理错误
    }
  };
}

export default ReconnectService;
